import _ from "lodash";
import moment from "moment";
import { Formik } from "formik";
import PropTypes from "prop-types";
import React, { Component } from "react";
import { compose } from "redux";
import { connect } from "react-redux";
import styled from "styled-components";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  Select,
  Step,
  StepLabel,
  Stepper,
  Table,
  TableBody,
  TableHead,
  TableRow,
  TableCell
} from "@material-ui/core";
import { PrimaryButton, SecondaryButton } from "../Global/Buttons";
import numberFormatter from "../../utils/numberFormatter";
import { getDealWithLoansBeingModified, completeLoanModification } from "../../store/state/deal";

const StyledTableCell = styled(TableCell)`
  padding-left: 10px !important;
  padding-right: 10px !important;
`;

const HeaderCell = styled(StyledTableCell)`
  font-weight: 700 !important;
  color: black !important;
`;

const HeightLimitedTableContainer = styled.div`
  max-height: 800px;
  overflow-y: auto;
  padding-left: 10px;
  padding-right: 10px;
`;

const StepInstructions = styled.div`
  margin-left: 20px;
  margin-bottom: 20px;
  color: rgba(0, 0, 0, 0.537254901960784);
`;

const ErrorMessage = styled(Grid)`
  color: red;
  width: 100%;
  padding-left: 20px;
  display: ${props => (props.display ? undefined : "none")};
`;

class CompleteLoanModificationModal extends Component {
  constructor(props) {
    super(props);

    this.state = { hasErrors: false, activeStep: 0 };
  }

  componentDidMount() {
    this.props.getDealWithLoansBeingModified(this.props.dealId);
  }

  completeLoanModification = deal => {
    this.props.completeLoanModification(deal);
    this.props.onClose();
  };

  validate = deal => {
    const errors = { loans: [] };
    let hasErrors = false;
    for (let loanIndex = 0; loanIndex < deal.loans.length; loanIndex++) {
      const loan = deal.loans[loanIndex];
      errors.loans.push({});
      if (!loan.action) {
        errors.loans[loanIndex].action = "Required";
        hasErrors = true;
      }
    }

    return hasErrors ? errors : null;
  };

  renderCompleteModifications = formik => {
    return (
      <>
        <StepInstructions>Select to confirm modifications made to the following loan(s):</StepInstructions>
        <HeightLimitedTableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <HeaderCell />
                <HeaderCell align="right">Modification Status</HeaderCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {formik.values.loans.map((loan, loanIndex) => (
                <TableRow key={loan.id}>
                  <HeaderCell component="th">
                    {_.get(loan, "loanCompanyBridge[0].company.name") || "N/A"} {" | "}
                    {loan.maturityDate ? moment(loan.maturityDate).format("MM/DD/YYYY") : "N/A"} {" | "}
                    {loan.rateTypeRef && loan.rateTypeRef.name ? loan.rateTypeRef.name : "N/A"} {" | "} {numberFormatter.maskCurrency(loan.amount) || "N/A"}
                  </HeaderCell>
                  <StyledTableCell>
                    <Select
                      placeholder="Select..."
                      autowidth
                      value={formik.values.loans[loanIndex].action}
                      onChange={eventArguements => {
                        formik.setFieldValue(`loans[${loanIndex}].action`, eventArguements.target.value);
                      }}
                      style={{ width: 150 }}
                    >
                      <MenuItem value="Complete">Complete</MenuItem>
                      <MenuItem value="In Progress">In Progress</MenuItem>
                      <MenuItem value="Cancel">Cancel</MenuItem>
                    </Select>
                  </StyledTableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </HeightLimitedTableContainer>
        <StepInstructions style={{ marginTop: 30 }}>
          NOTE: By selecting &quot;Cancel&quot; you will discard any modifications made to loans you selected above.
        </StepInstructions>
      </>
    );
  };

  renderConfirmChanges = formik => {
    return (
      <>
        <StepInstructions>
          You are about to confirm modifications for this loan. This action can only be reverted by a{" "}
          <a href="mailto:support@loanboss.com">LoanBoss Administrator</a>
        </StepInstructions>
        <HeightLimitedTableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <HeaderCell />
                <HeaderCell align="right">Modification Status</HeaderCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {formik.values.loans.map(loan => (
                <TableRow key={loan.id}>
                  <HeaderCell component="th">
                    {_.get(loan, "loanCompanyBridge[0].company.name") || "N/A"} {" | "}
                    {loan.maturityDate ? moment(loan.maturityDate).format("MM/DD/YYYY") : "N/A"} {" | "}
                    {loan.rateTypeRef && loan.rateTypeRef.name ? loan.rateTypeRef.name : "N/A"} {" | "} {numberFormatter.maskCurrency(loan.amount) || "N/A"}
                  </HeaderCell>
                  <StyledTableCell>{loan.action}</StyledTableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </HeightLimitedTableContainer>
        <StepInstructions style={{ marginTop: 30 }}>Are you sure you want to do this?</StepInstructions>
      </>
    );
  };

  renderDialog = formik => {
    return (
      <Dialog open fullWidth maxWidth="md">
        <DialogTitle>Confirm Modify</DialogTitle>
        <DialogContent>
          <Stepper activeStep={this.state.activeStep} alternativeLabel>
            <Step>
              <StepLabel>Complete Modifications</StepLabel>
            </Step>
            <Step>
              <StepLabel>Confirm Modifications</StepLabel>
            </Step>
          </Stepper>

          {this.state.activeStep === 0 && this.renderCompleteModifications(formik)}

          {this.state.activeStep === 1 && this.renderConfirmChanges(formik)}
        </DialogContent>
        <DialogActions>
          <ErrorMessage display={this.state.hasErrors}>Please enter values for all fields before continuing.</ErrorMessage>
          <SecondaryButton onClick={this.props.onClose}>Cancel</SecondaryButton>
          {this.state.activeStep === 0 && (
            <SecondaryButton
              onClick={() => {
                formik.validateForm().then(errors => {
                  if (_.isEmpty(errors)) {
                    this.setState({ hasErrors: false, activeStep: 1 });
                  } else {
                    this.setState({ hasErrors: true });
                  }
                });
              }}
            >
              Next
            </SecondaryButton>
          )}
          {this.state.activeStep === 1 && (
            <>
              <SecondaryButton onClick={() => this.setState({ activeStep: 0 })}>Back</SecondaryButton>
              <PrimaryButton onClick={formik.handleSubmit}>YES, CONFIRM</PrimaryButton>
            </>
          )}
        </DialogActions>
      </Dialog>
    );
  };

  render() {
    if (!this.props.deal) {
      return null;
    }
    return (
      <Formik
        initialValues={this.props.deal}
        onSubmit={deal => {
          this.props.onClose();
          this.props.completeLoanModification(deal);
        }}
        enableReinitialize
        validate={this.validate}
        render={this.renderDialog}
        validateOnChange={this.state.hasErrors}
      />
    );
  }
}

CompleteLoanModificationModal.propTypes = {
  onClose: PropTypes.func.isRequired,
  dealId: PropTypes.number.isRequired
};

function mapStateToProps({ deal }) {
  return {
    deal: deal.dealWithLoansBeingModified
  };
}

export default compose(connect(mapStateToProps, { getDealWithLoansBeingModified, completeLoanModification }))(CompleteLoanModificationModal);
